import { Placement } from '@popperjs/core'
import { Loader } from '@positivote/design-system/components/Loader'
import { Tooltip } from '@positivote/design-system/components/Tooltip'
import { useTheme } from '@positivote/design-system/hooks'
import { AlertErrorIcon } from '@positivote/design-system/icons/AlertError'
import { CheckCircleIcon } from '@positivote/design-system/icons/CheckCircle'
import { Breakpoint } from '@positivote/design-system/theme'
import { useEffect } from 'react'

import { i18n } from '@/common/i18n'
import { useShowIp, useShowSpeedTest } from '@/modules/requirements/hooks'

import { AccordionRequirements } from './AccordionRequirements'

export interface AccordionNetworkConnectionProps {
  onChangeNetworkInfos: (infos: { speed: number | undefined; ip: string | undefined }) => void
}

export function AccordionNetworkConnection({
  onChangeNetworkInfos
}: AccordionNetworkConnectionProps): JSX.Element {
  const showIp = useShowIp({})
  const showSpeedTest = useShowSpeedTest({})
  const { breakpoint } = useTheme()

  const tooltipPlacement: Placement = breakpoint >= Breakpoint.lg ? 'right' : 'left'

  const isLoading = showIp.isLoading || showSpeedTest.isLoading

  useEffect(() => {
    onChangeNetworkInfos({
      speed: showSpeedTest.data ?? 0,
      ip: showIp.data?.ip
    })
    // DOCS: This useeffect can only be used when changing states
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSpeedTest.data, showIp.data?.ip])

  return (
    <AccordionRequirements
      requirementsProps={{
        title: i18n().modules.requirements.components.accordionNetworkConnection.title,
        descriptions: [
          {
            key: 'ip',
            label: i18n().modules.requirements.components.accordionNetworkConnection.ipAddress,
            value:
              showIp.data?.ip ??
              i18n().modules.requirements.components.accordionNetworkConnection.unidentifiedIp,
            icon: showIp.isLoading ? (
              <Loader size={20} strokeWidth={2} css={{ right: 2 }} />
            ) : (
              <>
                {showIp.data?.ip ? (
                  <CheckCircleIcon fill="$success" />
                ) : (
                  <Tooltip
                    label={
                      i18n().modules.requirements.components.accordionNetworkConnection.tooltip
                        .ipError
                    }
                    placement={tooltipPlacement}
                    arrow
                  >
                    <AlertErrorIcon fill="$critical" />
                  </Tooltip>
                )}
              </>
            ),
            isLoading: showIp.isLoading
          },
          {
            key: 'internetSpeed',
            label: i18n().modules.requirements.components.accordionNetworkConnection.internetSpeed,
            value: showSpeedTest.data ? `${showSpeedTest.data} Mbps` : `${0} Mbps`,
            icon: showSpeedTest.isLoading ? (
              <Loader size={20} strokeWidth={2} css={{ right: 2 }} />
            ) : (
              <>
                {showSpeedTest.data ? (
                  <CheckCircleIcon fill="$success" />
                ) : (
                  <Tooltip
                    label={
                      i18n().modules.requirements.components.accordionNetworkConnection.tooltip
                        .speedTestError
                    }
                    placement={tooltipPlacement}
                    arrow
                  >
                    <AlertErrorIcon fill="$critical" />
                  </Tooltip>
                )}
              </>
            ),
            isLoading: showSpeedTest.isLoading
          }
        ]
      }}
      isLoading={isLoading}
    />
  )
}
